import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { EditIconGeneral, KebabMenuIcon } from '@/common/icons/general'
import {
  useDisableProviderUser,
  useResendProviderUserInvite,
} from '@/queries/provider-permission-management/mutations'
import { ProviderUser } from '@/queries/provider-permission-management/schema'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/shad/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shad/ui/dropdown-menu'
import { Loader2, SendHorizonal, UserRoundX } from 'lucide-react'
import { useState } from 'react'
import AdminAuthGuard from '../../../admin-auth-guard'

interface UserActionsMenuProps {
  user: ProviderUser
  onEditUser: (userId: number) => void
  programId: number
  providerId: string
}

export default function UserActionsMenu({
  user,
  onEditUser,
  programId,
  providerId,
}: UserActionsMenuProps) {
  const [open, setOpen] = useState(false)
  const [disableUserDialogOpen, setDisableUserDialogOpen] = useState(false)

  const resendInviteMutation = useResendProviderUserInvite()

  const disableUserMutation = useDisableProviderUser(providerId)

  const isResendingInvitation =
    resendInviteMutation.isPending &&
    resendInviteMutation.variables?.providerUserId === user.id

  const handleEditClick = () => {
    onEditUser(user.id)
    setOpen(false)
  }

  const handleResendInvitation = (e: React.MouseEvent) => {
    e.preventDefault()
    resendInviteMutation.mutate(
      {
        providerUserId: user.id,
        programId: programId,
      },
      {
        onSuccess: () => {
          setOpen(false)
        },
      }
    )
  }

  const handleDisableUser = () => {
    disableUserMutation.mutate(
      {
        providerUserId: user.id,
        programId: programId,
      },
      {
        onSuccess: () => {
          setDisableUserDialogOpen(false)
        },
      }
    )
  }

  return (
    <div className="flex justify-end">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild className="h-10 w-10 ">
          <button className="hover:bg-gray-100 rounded p-1">
            <KebabMenuIcon />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-full px-2">
          <div className="font-medium tracking-tight border-b border-gray-200 text-gray-800 text-sm py-2">
            {user.name ?? user.email}
          </div>
          <div className="py-1">
            <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={handleEditClick}
              >
                <EditIconGeneral className=" text-gray-400" />
                <span className="w-full font-normal text-gray-800">
                  Edit User Information
                </span>
              </DropdownMenuItem>
            </AdminAuthGuard>
            {user.status_name === 'Pending' && (
              <DropdownMenuItem
                className="disabled:opacity-50 disabled:hover:bg-none"
                disabled={isResendingInvitation}
                onClick={handleResendInvitation}
              >
                {isResendingInvitation ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : (
                  <SendHorizonal className=" text-gray-400" />
                )}
                <span className="font-normal text-gray-800">
                  {isResendingInvitation
                    ? 'Resending Invitation'
                    : 'Resend Invitation'}
                </span>
              </DropdownMenuItem>
            )}
            {user.status_name !== 'Inactive' && (
              <AdminAuthGuard
                requiredPermissions={['provider_management_edit']}
              >
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => {
                    setDisableUserDialogOpen(true)
                    setOpen(false)
                  }}
                >
                  <UserRoundX className=" text-gray-400" />
                  <span className="font-normal text-gray-800">
                    Disable User
                  </span>
                </DropdownMenuItem>
              </AdminAuthGuard>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={disableUserDialogOpen}
        onOpenChange={(open) => {
          if (!disableUserMutation.isPending) {
            setDisableUserDialogOpen(open)
          }
        }}
      >
        <DialogContent>
          <DialogTitle className="font-medium text-gray-800">
            Disable User
          </DialogTitle>
          <p className="text-gray-600">
            Are you sure you want to disable this user? This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDisableUserDialogOpen(false)}
              disabled={disableUserMutation.isPending}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                buttonTxt="Disable User"
                status={disableUserMutation.status}
                onClick={handleDisableUser}
                variant={'destructive'}
                submittingText="Disabling User..."
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
